import React, { forwardRef } from "react";
import styled from "styled-components";

/**
 * 並び替えハンドル（`drag_indicator` アイコン）。
 *
 * `SortableItem` の render-prop から得られる `handleProps` をスプレッドして使う。
 * テーブル行・カード・チップなど全てで見た目を揃えるため、共通のサイズ・色・
 * cursor を持つ。`touch-action: none` を必ず付け、モバイルでのスクロールと
 * ドラッグの誤判定を避ける。
 *
 * @example
 * ```tsx
 * <SortableItem id={id}>
 *   {({ setNodeRef, style, handleProps, isDragging }) => (
 *     <tr ref={setNodeRef} style={style}>
 *       <td><SortableDragHandle {...handleProps} disabled={!canDrag} /></td>
 *     </tr>
 *   )}
 * </SortableItem>
 * ```
 */
export interface SortableDragHandleProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  /** アイコンサイズ(px)。既定 18。 */
  size?: number;
  /** ハンドル無効化（cursor を default にする）。 */
  disabled?: boolean;
}

const Handle = styled.span.attrs({
  className: "material-symbols-rounded u-text-light-gray",
})<{ $size: number; $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => $size}px;
  line-height: 1;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "grab")};
  touch-action: none;
  user-select: none;

  &:active {
    cursor: ${({ $disabled }) => ($disabled ? "default" : "grabbing")};
  }
`;

const SortableDragHandle = forwardRef<HTMLSpanElement, SortableDragHandleProps>(
  ({ size = 18, disabled = false, "aria-label": ariaLabel, ...rest }, ref) => (
    <Handle
      ref={ref}
      $size={size}
      $disabled={disabled}
      aria-label={ariaLabel ?? "並び替え"}
      role="button"
      {...rest}
    >
      drag_indicator
    </Handle>
  ),
);

SortableDragHandle.displayName = "SortableDragHandle";

export default SortableDragHandle;
